/**
 * ListTableView - Sortable, groupable table of list entries
 */
import type { ReactElement } from 'react';

import clsx from 'clsx';
import { useAtom, useAtomValue } from 'jotai';
import { FaSortDown, FaSortUp } from 'react-icons/fa';

import type { CardGroup } from '@/lib/cardGrouping';
import type { EnrichedListCard, ListPriority } from '@/types/lists';

import {
  LIST_COLUMN_LABELS,
  LIST_COLUMN_ORDER,
  buildListGridTemplateColumns,
  listColumnVisibilityAtom,
  listSortAtom,
  type ListColumnKey,
  type ListSortField,
} from '../state/listViewSettings';
import ListTableRow from './ListTableRow';

/** Ownership sorts by available copies — the number a want-list user acts on. */
const COLUMN_SORT_FIELD: Partial<Record<ListColumnKey, ListSortField>> = {
  type: 'type',
  priority: 'priority',
  ownership: 'available',
  price: 'price',
};

const COLUMN_ALIGN: Partial<Record<ListColumnKey, string>> = {
  set: 'justify-end',
  type: 'justify-end',
  ownership: 'justify-end',
  price: 'justify-end',
};

export interface ListTableViewProps {
  groups: CardGroup<EnrichedListCard>[];
  showGroupHeaders: boolean;
  isPending: (cardId: string) => boolean;
  onIncrease: (card: EnrichedListCard) => void;
  onDecrease: (card: EnrichedListCard) => void;
  onRemove: (card: EnrichedListCard) => void;
  onPriorityChange: (card: EnrichedListCard, priority: ListPriority) => void;
  onNotesChange: (card: EnrichedListCard, notes: string | null) => void;
  onOpenDetails: (card: EnrichedListCard) => void;
}

export default function ListTableView({
  groups,
  showGroupHeaders,
  isPending,
  onIncrease,
  onDecrease,
  onRemove,
  onPriorityChange,
  onNotesChange,
  onOpenDetails,
}: ListTableViewProps): ReactElement {
  const columnVisibility = useAtomValue(listColumnVisibilityAtom);
  const [sort, setSort] = useAtom(listSortAtom);

  const gridTemplateColumns = buildListGridTemplateColumns(columnVisibility);
  const visibleColumns = LIST_COLUMN_ORDER.filter((column) => columnVisibility[column]);
  const columnCount = 4 + visibleColumns.length;

  const toggleSort = (field: ListSortField) =>
    setSort((prev) =>
      prev.field === field ? { field, direction: prev.direction === 'asc' ? 'desc' : 'asc' } : { field, direction: 'asc' },
    );

  const renderHeader = (label: string, field: ListSortField | undefined, align?: string) => {
    if (!field) {
      return (
        <div className={clsx('flex items-center px-2 py-2 text-xs font-medium text-zinc-500', align)}>
          <span className="truncate">{label}</span>
        </div>
      );
    }

    const isActive = sort.field === field;
    return (
      <div className={clsx('flex items-center px-2 py-2', align)}>
        <button
          type="button"
          onClick={() => toggleSort(field)}
          aria-label={`Sort by ${label}`}
          className={clsx(
            'focus-visible:ring-aether flex items-center gap-1 rounded text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2',
            isActive ? 'text-zinc-200' : 'text-zinc-500 hover:text-zinc-300',
          )}
        >
          <span className="truncate">{label}</span>
          {isActive && (sort.direction === 'asc' ? <FaSortUp className="h-3 w-3 shrink-0" /> : <FaSortDown className="h-3 w-3 shrink-0" />)}
        </button>
      </div>
    );
  };

  return (
    <div className="w-full" style={{ display: 'grid', gridTemplateColumns }}>
      {/* Header */}
      <div className="contents">
        {renderHeader('Qty', 'quantity', 'justify-center')}
        <div aria-hidden className="px-2 py-2" />
        {renderHeader('Name', 'name')}
        {visibleColumns.map((column) => (
          <div key={column} className="contents">
            {renderHeader(LIST_COLUMN_LABELS[column], COLUMN_SORT_FIELD[column], COLUMN_ALIGN[column])}
          </div>
        ))}
        <div aria-hidden className="px-2 py-2" />
      </div>

      {groups.map((group) => (
        <div key={group.key} className="contents">
          {showGroupHeaders && (
            <h3
              className="mt-4 border-b border-zinc-800 px-2 pb-1 text-xs font-medium tracking-wide text-zinc-400 uppercase"
              style={{ gridColumn: `span ${columnCount}` }}
            >
              {group.label} <span className="text-zinc-600">({group.cards.length})</span>
            </h3>
          )}
          {group.cards.map((card) => (
            <ListTableRow
              key={card.id}
              card={card}
              columnVisibility={columnVisibility}
              isPending={isPending(card.id)}
              onIncrease={() => onIncrease(card)}
              onDecrease={() => onDecrease(card)}
              onRemove={() => onRemove(card)}
              onPriorityChange={(priority) => onPriorityChange(card, priority)}
              onNotesChange={(notes) => onNotesChange(card, notes)}
              onOpenDetails={() => onOpenDetails(card)}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
